/**
 * The service exchange page's route export (G2 test d, ADR 9010/9041) — `docs/adr/6015`.
 *
 * Every evaluated policy of the forecast run the URL names: rule, level, whether the forecast
 * stored the level, and what it held, served and left over. An empty file while the read is not
 * pinned or no forecast run is named, the filters saying so. No chart import: the manifest imports
 * this file, and the manifest is in every page's first bundle.
 */

import { callOperation } from '@/api';
import {
  EXCHANGE_RULES, SERVICE_EXCHANGE_READ, serviceExchangeOf, serviceExchangeParams, type ExchangePolicy, type ServiceExchange,
  type ServiceExchangeRead,
} from '@/api/pending/service-tradeoff';
import { buildVersion, NO_PRINCIPAL, NOT_PUBLISHED, type ExportCell, type ExportColumn, type ExportRequest, type ProvenanceFilter } from '@/export';
import type { ExportScope } from '@/features/manifest';

import { readExchangeScope } from './scope';

export const EXCHANGE_EXPORT_ID = 'inseason.serviceExchange.policies';

/** The policies in the curve's order: the rules as declared, each by level. Pure. */
export function orderedPolicies(policies: readonly ExchangePolicy[]): readonly ExchangePolicy[] {
  return [...policies].sort((a, b) => (EXCHANGE_RULES.indexOf(a.rule) - EXCHANGE_RULES.indexOf(b.rule)) || a.level - b.level);
}

export const POLICY_COLUMNS: readonly ExportColumn[] = [
  { key: 'rule', headerKey: 'inseason.exchange.column.rule', descriptionKey: 'inseason.exchange.columnGuide.rule', kind: 'text' },
  { key: 'level', headerKey: 'inseason.exchange.column.level', descriptionKey: 'inseason.exchange.columnGuide.level', kind: 'number' },
  { key: 'from_stored_level', headerKey: 'inseason.exchange.column.source', kind: 'text', descriptionKey: 'inseason.exchange.columnGuide.source' },
  { key: 'pairs_held', headerKey: 'inseason.exchange.column.pairsHeld', kind: 'number', descriptionKey: 'inseason.exchange.columnGuide.pairsHeld' },
  { key: 'pairs_served', headerKey: 'inseason.exchange.column.pairsServed', descriptionKey: 'inseason.exchange.columnGuide.pairsServed', kind: 'number' },
  { key: 'demand_pairs', headerKey: 'inseason.exchange.column.demand', kind: 'number', descriptionKey: 'inseason.exchange.columnGuide.demand' },
  { key: 'fill_rate', headerKey: 'inseason.exchange.column.fillRate', descriptionKey: 'inseason.exchange.columnGuide.fillRate', kind: 'number' },
  { key: 'cycle_service', headerKey: 'inseason.exchange.column.cycleService', descriptionKey: 'inseason.exchange.columnGuide.cycleService', kind: 'number' },
  { key: 'leftover_pairs', headerKey: 'inseason.exchange.column.leftover', descriptionKey: 'inseason.exchange.columnGuide.leftover', kind: 'number' },
  { key: 'sizes_stocked_share', headerKey: 'inseason.exchange.column.sizesStocked', descriptionKey: 'inseason.exchange.columnGuide.sizesStocked', kind: 'number' },
];

const num = (value: number | null): ExportCell => (value === null ? { kind: 'missing', state: 'not-reported' } : { kind: 'number', raw: value });

export function policyRow(policy: ExchangePolicy): readonly ExportCell[] {
  return [
    { kind: 'text', value: policy.rule }, num(policy.level), { kind: 'text', value: policy.stored ? 'true' : 'false' },
    num(policy.pairsHeld), num(policy.pairsServed), num(policy.demandPairs), num(policy.fillRate), num(policy.cycleService),
    num(policy.leftoverPairs), num(policy.sizesStockedShare),
  ];
}

const DEFINITION = 'bata-ui issue 307 / 308: each door × article × size stocks the forecast quantile at the level, made whole pairs by the rule; served = min(stock, pairs sold) in the forecast period; fill rate = Σ served ÷ Σ sold; cycle service = share of cells whose sales fit in the stock, as GET /inseason/service-exchange/{forecast_run_id} states them (bata-platform 571). Informs the service-level constraint; recommends no level (reqs/07 §3.4).';

function request(rows: readonly (readonly ExportCell[])[], filters: readonly ProvenanceFilter[], exchange: ServiceExchange | null): ExportRequest {
  return {
    id: EXCHANGE_EXPORT_ID,
    titleKey: 'inseason.exchange.exportTitle',
    columns: POLICY_COLUMNS,
    rows: () => rows,
    rowCount: rows.length,
    provenance: {
      filters,
      hierarchyNode: NOT_PUBLISHED,
      timeWindow: exchange === null ? NOT_PUBLISHED : { from: exchange.period, to: exchange.period },
      scenario: NOT_PUBLISHED,
      planVersionId: NOT_PUBLISHED,
      measureDefinitions: [{ measure: EXCHANGE_EXPORT_ID, definition: DEFINITION, registerVersion: exchange?.modelVersionId ?? '' }],
      generatedAt: new Date().toISOString(),
      generatedBy: NO_PRINCIPAL,
      platformVersion: buildVersion(),
    },
    perimeter: { filters, sort: [], visibleColumns: POLICY_COLUMNS.map((column) => column.key), drillPath: [], densityMode: 'compact' },
  };
}

export async function serviceExchangeExport(given?: ExportScope, read: ServiceExchangeRead | null = SERVICE_EXCHANGE_READ): Promise<ExportRequest> {
  const runId = readExchangeScope(given?.search ?? '').forecastRunId;
  const filters: ProvenanceFilter[] = [
    runId === null ? { field: 'forecast_run_id', operator: 'unset', value: '' } : { field: 'forecast_run_id', operator: 'equals', value: runId },
  ];
  if (read === null || runId === null) return request([], filters, null);
  const exchange = serviceExchangeOf(await callOperation(read, serviceExchangeParams(runId), {}));
  return request(orderedPolicies(exchange.policies).map(policyRow), filters, exchange);
}
